import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MatTableDataSource as MatTableDataSource } from '@angular/material/table';
import { OrderDocument } from '../../../../shared/objects/order/orderDocument';
import { DocumentStatus } from '../../../../shared/objects/enum/documentStatus';
import { OrderDocumentService } from '../shared/order-document.service';
import { OrderDocumentType } from '../../../../shared/objects/order/orderDocumentType';
import { CmrStatus } from '../../../../shared/objects/enum/cmrStatus';
import { Constants } from '../../../../shared/const/constants';
import { FileInputComponent } from '../../../../shared/components/file-input/file-input.component';
import { LoadingIndicatorService } from '../../../../core/services/loading-indicator.service';
import { GlobalResponsivenessService } from '../../../../core/services/global-responsiveness.service';
import { AuthService } from '../../../../core/services/auth.service';
import { CmrValidationService } from '../shared/cmr-validation.service';
import {
  CmrValidationAction,
  CmrValidationActionLabel,
} from '../../../../shared/objects/enum/cmrValidationAction';

@Component({
  selector: 'app-order-documents',
  templateUrl: './order-documents.component.html',
  styleUrls: ['./order-documents.component.css'],
  standalone: false,
})
export class OrderDocumentsComponent implements OnInit {
  @Input() editMode: boolean;
  @Input() orderId: number;
  @Input() orderCmrStatus: CmrStatus;
  @Input() customerId: number;

  @ViewChild(FileInputComponent, { static: true })
  fileInputControl: FileInputComponent;
  isMobile: boolean = false;
  isTabletPortrait: boolean = false;
  columnsToDisplay = ['status', 'documentType', 'uploadedDocument', 'actions'];
  documentDatasource = new MatTableDataSource<OrderDocument>();
  requiredDocuments: any;

  get otherOrderDocument() {
    return this.resolveMissingDocument(
      this.documentService.allDocumentTypes.find(
        type => type.code === Constants.OTHER_DOCUMENT_IDENTIFIER,
      ),
    );
  }
  get isImpersonated() {
    return this.authService.isImpersonated();
  }
  docStatus = DocumentStatus;
  documents: OrderDocument[];
  cmrStatus = CmrStatus;
  selectedDocument: OrderDocument;
  private documentsBeforeScroll = 3;

  validExtensions = Constants.VALID_ORDER_DOCUMENT_EXTENSION;
  uploadInProgress = false;
  uploadFeedbackMessage: string | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private documentService: OrderDocumentService,
    private loadingIndicatorService: LoadingIndicatorService,
    private globalResponsive: GlobalResponsivenessService,
    private authService: AuthService,
    private cmrValidationService: CmrValidationService,
  ) {
    var attributes = this.globalResponsive.getResolutionAttributes();
    this.isTabletPortrait = attributes.isPortraitTablet;
    this.isMobile = attributes.isMobile;
  }

  ngOnInit() {
    this.globalResponsive.isMobile$.subscribe(
      isMobile => (this.isMobile = isMobile),
    );
    this.globalResponsive.isPortraitTablet$.subscribe(
      isTabletPortrait => (this.isTabletPortrait = isTabletPortrait),
    );
    this.documentService
      .getRequiredDocumentTypes(this.orderId)
      .subscribe(requiredDocs => {
        this.requiredDocuments = requiredDocs;
      });
    this.refreshDocumentList();
  }

  processOrderDocuments(
    documentTypes: OrderDocumentType[],
    orderDocuments: OrderDocument[],
  ): OrderDocument[] {
    const finalDocuments = new Array<OrderDocument>();
    if (!documentTypes || documentTypes.length === 0) {
      return [...finalDocuments];
    }
    documentTypes.forEach(type => {
      const documents = orderDocuments.filter(
        o => o.orderDocumentTypeId === type.id,
      );
      if (!!documents && documents.length > 0) {
        documents.forEach(doc => {
          finalDocuments.push(doc);
        });
      } else if (type.code !== Constants.OTHER_DOCUMENT_IDENTIFIER) {
        const doc = this.resolveMissingDocument(type);
        finalDocuments.push(doc);
      }
    });

    return [...finalDocuments];
  }

  resolveMissingDocument(type: OrderDocumentType): OrderDocument {
    let orderDocument = new OrderDocument();
    orderDocument = {
      ...orderDocument,
      id: 0,
      status:
        this.requiredDocuments?.findIndex(req => req == type.id) != -1
          ? DocumentStatus.Missing
          : type.externalNameForbusinessUnit ===
              this.documentService.cmrExternalNameForBusinessUnit?.externalName
            ? this.orderCmrStatus === this.cmrStatus.Missing
              ? DocumentStatus.Missing
              : DocumentStatus.NotRequired
            : type.required
              ? DocumentStatus.Missing
              : DocumentStatus.NotRequired,
      type: this.documentService.getExternalDocumentTypeBy(type),
      fileName: '',
      description: 'Added from Carrier Portal',
      orderDocumentTypeId: type.id,
    };

    return orderDocument;
  }

  isExpandablePanel(element: OrderDocument) {
    if (element.fileName != null && element.fileName != '') {
      return true;
    }
    return false;
  }

  get atLeastOneDocumentIsMandatory(): boolean {
    if (!this.documents) return false;
    return this.documents.some(doc => doc.status === this.docStatus.Missing);
  }

  shouldScrollBeDisplayed(): boolean {
    if (!this.documents) {
      return false;
    }
    return this.documents.length > this.documentsBeforeScroll;
  }

  addOtherDocument(): void {
    this.browse(this.otherOrderDocument);
  }

  browse(orderDocument: OrderDocument): void {
    this.fileInputControl.selectFile();
    this.selectedDocument = orderDocument;
  }

  upload(file: File): void {
    this.uploadFeedbackMessage = null;
    this.setupDocumentForTesting(this.selectedDocument);
    this.uploadInProgress = true;
    this.loadingIndicatorService.showFetchData();

    if (this.selectedDocument.type === Constants.CMR_DOCUMENT_IDENTIFIER) {
      this.cmrValidationService
        .customerRequiresCmrValidation(this.customerId)
        .subscribe(
          required => {
            if (!required) {
              this.performUpload(file);
              return;
            }
            this.cmrValidationService.validateCmr(file).subscribe(
              result => {
                if (!result.accepted) {
                  this.uploadFeedbackMessage = this.resolveValidationFeedback(
              		result.action,
            	  );
                  this.uploadInProgress = false;
                  this.loadingIndicatorService.hideFetchData();
                  return;
                }

                this.performUpload(
                  file,
                  result.action == CmrValidationAction.NotifyFmsUser,
                );
              },
              _ => this.handleUploadError(),
            );
          },
          _ => this.handleUploadError(),
        );
    } else {
      this.performUpload(file);
    }
  }

  private performUpload(file: File, notifyFmsUser = false): void {
    this.documentService
      .uploadFile(this.orderId, this.selectedDocument, file)
      .subscribe(
        () => {
          if (notifyFmsUser) {
            this.cmrValidationService
              .notifyFmsUserOfCmrValidation(this.orderId)
              .subscribe({ error: () => null });
          }
          this.refreshDocumentList();
          this.uploadInProgress = false;
          this.loadingIndicatorService.hideFetchData();
        },
        _ => this.handleUploadError(),
      );
  }

  private handleUploadError(): void {
    this.uploadFeedbackMessage = 'SHARED.DOCUMENTS-UPLOAD-TEXT-ERROR';
    this.uploadInProgress = false;
    this.loadingIndicatorService.hideFetchData();
  }

  private resolveValidationFeedback(action: CmrValidationAction): string {
    return CmrValidationActionLabel.get(action) ?? 'SHARED.CMR-NOT-RECOGNISED';
  }

  private refreshDocumentList(): void {
    this.documentService
      .getDocumentsBy(this.orderId)
      .subscribe(orderDocuments => {
        this.documents = this.processOrderDocuments(
          this.documentService.allDocumentTypes,
          orderDocuments,
        );
        this.documentDatasource.data = this.documents;
      });
  }

  view(documentId: number, fileName: string): void {
    this.documentService.viewFile(documentId, fileName);
  }

  formatName(name: string): string {
    var nameLimit = 0;
    if (this.isMobile) nameLimit = 30;
    else if (this.isTabletPortrait) nameLimit = 40;
    return name.length > nameLimit ? `${name.substr(0, nameLimit)}...` : name;
  }

  openDocPanel(panel: Element) {
    var element = document.getElementById(panel.id);
    setTimeout(() => {
      element.scrollIntoView({ block: 'nearest' });
    }, 250);
  }

  private setupDocumentForTesting(document: OrderDocument): void {
    this.selectedDocument = !!document ? document : this.otherOrderDocument;
  }
}
